import type { TimeEntry } from '../../../api/types/timeEntry.types';
import { toLocalDateStr } from '../../../shared/lib/dateUtils';
import type { DayGroup, ProjectGroup, WeekGroup } from '../types/tracker.types';

// ─── Time formatting ──────────────────────────────────────────────────────────

export function toHHMM(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function buildDateTimeISO(dateStr: string, timeStr: string): string {
  return new Date(`${dateStr}T${timeStr}:00`).toISOString();
}

// Returns null when input is empty or does not resolve to a valid HH:mm time
// (hours 00-23, minutes 00-59). Callers must fall back to the original saved
// value on null — never silently default to "00:00".
//
// Two modes:
//   Colon present  → strict HH:MM parsing. Letters on either side are invalid.
//                    Minutes cannot be empty (e.g. "12:" is invalid).
//                    Empty hours are treated as 0 (e.g. ":30" → "00:30").
//   No colon       → lenient digit-only parsing. Non-digit chars are stripped.
export function parseTimeDigits(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const pad = (n: number) => n.toString().padStart(2, '0');

  if (trimmed.includes(':')) {
    const colonIdx = trimmed.indexOf(':');
    const hourPart = trimmed.slice(0, colonIdx);
    const minPart  = trimmed.slice(colonIdx + 1);

    // Letters anywhere on either side → invalid
    if (/[a-zA-Z]/.test(hourPart) || /[a-zA-Z]/.test(minPart)) return null;

    const hourDigits = hourPart.replace(/\D/g, '');
    // Strip extra colons from the minutes side (handles "22::05")
    const minDigits  = minPart.replace(/:/g, '').replace(/\D/g, '').slice(0, 2);

    // Minutes must have at least one digit ("12:" is invalid)
    if (!minDigits) return null;

    const hh = hourDigits ? parseInt(hourDigits, 10) : 0;
    const mm = parseInt(minDigits, 10);

    if (hh <= 23 && mm <= 59) return `${pad(hh)}:${pad(mm)}`;
    return null;
  }

  // No colon: digit-only parsing (lenient)
  const digits = trimmed.replace(/\D/g, '').slice(0, 4);
  if (!digits) return null;

  if (digits.length === 1) {
    return `${pad(parseInt(digits, 10))}:00`;
  }

  if (digits.length === 2) {
    const val = parseInt(digits, 10);
    if (val <= 23) return `${pad(val)}:00`;
    // > 23 → treat each digit as its own H and M (e.g. "28" → "02:08", "45" → "04:05")
    return `${pad(parseInt(digits[0], 10))}:${pad(parseInt(digits[1], 10))}`;
  }

  if (digits.length === 3) {
    const h  = parseInt(digits[0], 10);
    const mm = parseInt(digits.slice(1), 10);
    if (h <= 23 && mm <= 59) return `${pad(h)}:${pad(mm)}`;
    return null;
  }

  // 4 digits
  const hh = parseInt(digits.slice(0, 2), 10);
  const mm = parseInt(digits.slice(2), 10);
  if (hh <= 23 && mm <= 59) return `${pad(hh)}:${pad(mm)}`;
  return null;
}

// Parses a user-typed duration string into total seconds.
// Accepts:
//   Colon forms  → "H:MM" or "H:MM:SS"  (e.g. "2:30" → 9000, "2:30:15" → 9015)
//   Plain digits → treated as whole minutes (e.g. "90" → 5400, "30" → 1800)
// Returns null for unparseable or negative input.
export function parseDurationToSecs(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (trimmed.includes(':')) {
    const parts = trimmed.split(':');
    if (parts.length === 2) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (isNaN(h) || isNaN(m) || h < 0 || m < 0 || m >= 60) return null;
      return h * 3600 + m * 60;
    }
    if (parts.length === 3) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parseInt(parts[2], 10);
      if (isNaN(h) || isNaN(m) || isNaN(s) || h < 0 || m < 0 || s < 0 || m >= 60 || s >= 60) return null;
      return h * 3600 + m * 60 + s;
    }
    return null;
  }

  // No colons — treat raw digits as whole minutes
  const mins = parseInt(trimmed.replace(/\D/g, ''), 10);
  if (isNaN(mins) || mins < 0) return null;
  return mins * 60;
}

// ─── Entry grouping ───────────────────────────────────────────────────────────

function localDateStr(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function groupByDay(entries: TimeEntry[]): DayGroup[] {
  const byDay: Record<string, TimeEntry[]> = {};

  for (const e of entries) {
    const day = localDateStr(e.startedAt);
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(e);
  }

  return Object.keys(byDay)
    .sort((a, b) => (a < b ? 1 : -1))
    .map((date) => {
      const dayEntries = byDay[date];
      const totalSecs = dayEntries.reduce((sum, e) => sum + (e.durationSecs ?? 0), 0);

      const byProject: Record<string, ProjectGroup> = {};
      for (const e of dayEntries) {
        const key = e.projectId ?? '__none__';
        if (!byProject[key]) {
          byProject[key] = {
            projectId: e.projectId,
            projectName: e.projectName,
            projectColor: e.projectColor,
            clientName: e.clientName,
            entries: [],
          };
        }
        byProject[key].entries.push(e);
      }

      return { date, totalSecs, projectGroups: Object.values(byProject) };
    });
}

// ─── Week helpers ─────────────────────────────────────────────────────────────

export function groupByWeek(dayGroups: DayGroup[]): WeekGroup[] {
  const byWeek: Record<string, DayGroup[]> = {};

  for (const group of dayGroups) {
    const d = new Date(group.date + 'T00:00:00');
    const weekStart = startOfWeek(d);
    const key = toLocalDateStr(weekStart);
    if (!byWeek[key]) byWeek[key] = [];
    byWeek[key].push(group);
  }

  return Object.keys(byWeek)
    .sort((a, b) => (a < b ? 1 : -1))
    .map((weekStartStr) => {
      const groups = byWeek[weekStartStr];
      const weekStart = new Date(weekStartStr + 'T00:00:00');
      const weekEnd = addDays(weekStart, 6);
      const totalSecs = groups.reduce((sum, g) => sum + g.totalSecs, 0);
      return {
        weekStart: weekStartStr,
        weekEnd: toLocalDateStr(weekEnd),
        totalSecs,
        dayGroups: groups,
      };
    });
}

export function formatWeekRange(weekStart: string, weekEnd: string): string {
  const from = new Date(weekStart + 'T00:00:00');
  const to = new Date(weekEnd + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${from.toLocaleDateString('en-US', opts)} - ${to.toLocaleDateString('en-US', opts)}`;
}

export function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Mon
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function toDateStr(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function weekLabel(from: Date, to: Date): string {
  const sameMonth = from.getMonth() === to.getMonth();
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (sameMonth) {
    return `${from.toLocaleDateString('en-US', opts)} – ${to.getDate()}`;
  }
  return `${from.toLocaleDateString('en-US', opts)} – ${to.toLocaleDateString('en-US', opts)}`;
}

export function isCurrentWeek(weekStart: Date): boolean {
  return toDateStr(weekStart) === toDateStr(startOfWeek(new Date()));
}

// ─── Month helpers ────────────────────────────────────────────────────────────

export function startOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function isCurrentMonth(date: Date): boolean {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}
